import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, Lock, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const AdminLogin = () => {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (admin) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/admin", { replace: true });
    } catch (e) {
      setErr(e.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-5 relative overflow-hidden" style={{ backgroundColor: "var(--bg)" }}>
      <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-[color:var(--coral-soft)] mk-float" />
      <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[color:var(--mint)] mk-float" style={{ animationDelay: "1.5s" }} />

      <form
        onSubmit={onSubmit}
        className="relative w-full max-w-md p-8 md:p-10 rounded-[26px] bg-white border border-black/10 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.3)]"
      >
        <div className="flex items-center gap-2 mb-2">
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-[color:var(--ink)] text-[color:var(--bg)] font-display font-bold">
            m
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[color:var(--coral)]" />
          </span>
          <span className="font-display text-xl font-semibold">Misbah.</span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold leading-tight">
          Welcome back.
        </h1>
        <p className="mt-2 text-sm text-[color:var(--muted)]">Sign in to edit your site.</p>

        <div className="mt-8 space-y-5">
          <Field icon={Mail} label="Email" type="email" value={email} onChange={setEmail} placeholder="misbah@misbahkhan.com" />
          <Field icon={Lock} label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
        </div>

        {err && (
          <div className="mt-4 text-sm text-[color:var(--coral)] bg-[color:var(--coral-soft)]/40 rounded-md px-3 py-2 border border-[color:var(--coral)]/20">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="mt-7 w-full mk-btn mk-btn-primary justify-center disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
          <ArrowRight className="w-4 h-4" />
        </button>

        <a href="/" className="mt-6 block text-center text-xs text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors">
          ← Back to website
        </a>
      </form>
    </div>
  );
};

const Field = ({ icon: Icon, label, type, value, onChange, placeholder }) => (
  <label className="block">
    <span className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)]">
      {label}
    </span>
    <div className="mt-1.5 flex items-center gap-3 border-b border-black/25 focus-within:border-[color:var(--coral)] transition-colors">
      <Icon className="w-4 h-4 text-[color:var(--muted)]" />
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent py-3 outline-none text-base placeholder:text-[color:var(--muted)]"
        autoComplete={type === "password" ? "current-password" : "email"}
        required
      />
    </div>
  </label>
);

export default AdminLogin;
